import { useCallback, useEffect, useState } from 'react'

/** What `redux-saga-callback` hands an `onComplete` when a write saga finishes. */
export interface SaveOutcome {
  error?: unknown
  cancelled?: boolean
  /** The write saga's return value — the API envelope, or null if it threw. */
  data?: { data?: unknown; error?: unknown } | null
}

export interface DrawerPanel {
  open: boolean
  close: () => void
  /** Pass to the drawer's `afterOpenChange` so unmounting waits for the slide-out. */
  onAfterOpenChange: (isOpen: boolean) => void
  /** Close only once the write landed, so a rejected save keeps the user's input. */
  closeOnSaved: (outcome?: SaveOutcome) => void
}

/**
 * Drives a drawer the parent mounts on demand: it slides in on mount, and the
 * parent's `onClose` is deferred until the slide-out finishes.
 */
export default function useDrawerPanel(onClose: () => void): DrawerPanel {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    setOpen(true)
  }, [])

  const close = useCallback(() => setOpen(false), [])

  const onAfterOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) onClose()
    },
    [onClose],
  )

  const closeOnSaved = useCallback((outcome?: SaveOutcome) => {
    if (outcome?.error || outcome?.cancelled) return
    // The write sagas return the response either way; only a success carries `data`.
    if (outcome?.data?.data) setOpen(false)
  }, [])

  return { open, close, onAfterOpenChange, closeOnSaved }
}
